// plan-17 S02: phosphor burn-in pass — long-session ghost accumulation
// 1/8 res offscreen accumulator, 60s warm-up, decays on tab-hide

import type { DrawPass } from '../canvas'
import { load, save } from '../../core/save'

const BLEND_RATE = 0.008   // alpha per 4s tick
const RENDER_ALPHA = 0.06  // composite onto main canvas
const ALPHA_CAP = 0.12     // max accumulation
const TICK_MS = 4000       // accumulation interval
const WARMUP_MS = 60000    // 60s focus before accumulation starts
const DECAY_FACTOR = 0.5   // halve on long tab-hide

function toBase32(n: number): string {
  const chars = '0123456789abcdefghijklmnopqrstuv'
  let s = ''
  for (let i = 0; i < 4; i++) {
    s = chars[n & 0x1f] + s
    n = Math.floor(n / 32)
  }
  return s
}

export function createBurnIn(reduced: boolean): DrawPass {
  if (reduced) return () => {}

  let offscreen: HTMLCanvasElement | null = null
  let offCtx: CanvasRenderingContext2D | null = null
  let prevW = 0
  let prevH = 0
  let focusStart = 0
  let accumulating = false
  let lastTick = 0
  let hiddenSince = 0
  let currentAlpha = 0

  // ensure deterministic burn pattern seed from save
  const s = load()
  if (!s.polishBurnInPattern) {
    s.polishBurnInPattern = toBase32(s.firstSeenAt)
    save(s)
  }

  // track focus/visibility
  const onFocus = (): void => {
    focusStart = Date.now()
    if (hiddenSince > 0) {
      const hiddenMs = Date.now() - hiddenSince
      if (hiddenMs > WARMUP_MS) {
        // decay the buffer
        if (offCtx && offscreen) {
          offCtx.globalAlpha = DECAY_FACTOR
          offCtx.fillStyle = 'rgba(0,0,0,1)'
          offCtx.fillRect(0, 0, offscreen.width, offscreen.height)
          offCtx.globalAlpha = 1
          currentAlpha *= DECAY_FACTOR
        }
      }
      hiddenSince = 0
    }
  }
  const onBlur = (): void => { hiddenSince = Date.now() }

  window.addEventListener('focus', onFocus)
  window.addEventListener('blur', onBlur)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenSince = Date.now()
    } else {
      onFocus()
    }
  })

  // seed focus start
  focusStart = Date.now()

  return (ctx, w, h, _t): void => {
    // check warmup
    if (!accumulating) {
      if (document.hasFocus() && Date.now() - focusStart >= WARMUP_MS) {
        accumulating = true
        lastTick = Date.now()
      } else {
        return
      }
    }

    // resize offscreen at 1/8 resolution
    const ow = Math.ceil(w / 8)
    const oh = Math.ceil(h / 8)

    if (!offscreen || prevW !== ow || prevH !== oh) {
      offscreen = document.createElement('canvas')
      offscreen.width = ow
      offscreen.height = oh
      offCtx = offscreen.getContext('2d', { alpha: true })!
      prevW = ow
      prevH = oh
    }

    if (!offCtx) return

    // accumulate every TICK_MS
    const now = Date.now()
    if (now - lastTick >= TICK_MS && document.hasFocus()) {
      lastTick = now
      currentAlpha = Math.min(currentAlpha + BLEND_RATE, ALPHA_CAP)

      // blend live composite into accumulator at low alpha
      offCtx.globalAlpha = BLEND_RATE
      offCtx.drawImage(ctx.canvas, 0, 0, ow, oh)
      offCtx.globalAlpha = 1
    }

    // render accumulator onto main canvas via screen blend
    if (currentAlpha > 0.001) {
      ctx.globalCompositeOperation = 'screen'
      ctx.globalAlpha = RENDER_ALPHA
      ctx.drawImage(offscreen, 0, 0, ow, oh, 0, 0, w, h)
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    }
  }
}
